import { Component, computed, DestroyRef, inject, ChangeDetectionStrategy} from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { outputToObservable, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { LoginComponent } from '../login/login.component';
import { MatToolbar } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { CreateIssueModalComponent } from '../issue/create-issue-modal/create-issue-modal.component';
import { UserStore } from '../../stores/user.store';
import { RolesStore } from '../../stores/roles.store';
import { AccessStore } from '../../stores/access.store';
import { FilterOrganizationContextComponent } from '../filter-organization-context/filter-organization-context.component';
import { UserProfileMenuComponent } from '../user-profile-menu/user-profile-menu.component';
import { MyWorkMenuComponent } from '../my-work-menu/my-work-menu.component';
import { NotificationsMenuComponent } from '../notifications-menu/notifications-menu.component';
import { MatIcon } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { SearchBarComponent } from '../search-bar/search-bar.component';
import { NotificationService } from '../../services/notification.service';

@Component({
    selector: 'gcp-workspace-navbar',
    imports: [
        LoginComponent,
        MatToolbar,
        MatButtonModule,
        MatDialogModule,
        FilterOrganizationContextComponent,
        UserProfileMenuComponent,
        MyWorkMenuComponent,
        NotificationsMenuComponent,
        MatIcon,
        MatBadgeModule,
        MatTooltipModule,
        CdkOverlayOrigin,
        SearchBarComponent,
    ],
    templateUrl: './navbar.component.html',
    styleUrl: './navbar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent {
  private auth = inject(AuthService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  isAuthenticated = toSignal(this.auth.isAuthenticated$, { initialValue: false });

  userStore = inject(UserStore);
  private readonly rolesStore = inject(RolesStore);
  private readonly accessStore = inject(AccessStore);

  readonly notificationService = inject(NotificationService);

  showMyWork = false;
  showNotifications = false;

  currentUser = computed(() => this.userStore.currentUser());

  // Cross-org pivoting beyond one's own memberships is the uber capability
  // (the backend authorizes it on the global grant), so the switcher follows
  // isUber rather than the legacy userRole table.
  isAtLeastAdminTenant = computed(() => this.accessStore.isUber());

  // Show the organization context switcher when the user can actually switch:
  // tenant/uber admins (existing behaviour) OR any user who is an active member
  // of more than one organization. The latter is what lets non-admin multi-org
  // users move between their organizations.
  showOrgSwitcher = computed(
    () =>
      this.isAtLeastAdminTenant() ||
      this.rolesStore.activeMembershipOrgIds().length > 1
  );

  createIssue() {
    const dialogRef = this.dialog.open(CreateIssueModalComponent, {
      panelClass: 'create-issue-modal',
      width: '90vw',
      maxWidth: '98vw',
      height: '85vh',
      autoFocus: false,
      disableClose: false,
    });
    outputToObservable(dialogRef.componentInstance.closeDialog).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((close) => {
      if (close) {
        dialogRef.close();
      }
    });
  }
}
